import { useState, useEffect, useRef } from "react";
import {
    Plus,
    Trash2,
    ChevronUp,
    ChevronDown,
    ListOrdered,
    GripVertical,
    Lightbulb,
    Target,
    MessageSquare,
    ArrowRight,
    CheckCircle2,
    Circle,
    Clock,
    Sparkles,
} from "lucide-react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Textarea } from "@component/textarea";
import { Label } from "@component/label";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@component/collapsible";
import GenerateInterviewDialog from "@container/dialog/dialog-generate-interview";
import { GenerateInterviewSectionResponse, InterviewAssessment, Section } from "@type/assessments";
import { generateInterviewSections } from "@service/assessment";
import { catchErrorLog } from "@util/sentry";
import { useSnackbar } from "@hook/use-snackbar";
import { renderTextWithLineBreaks } from "@util/format";

interface InterviewQuestion {
    id: number;
    question: string;
    followUpInstructions?: string[];
    isExpanded?: boolean;
}

interface InterviewSection {
    id: number;
    title: string;
    maxDuration: number; // Duration in minutes for this section
    instructions: string;
    evaluationCriteria?: string; // Optional evaluation criteria field
    questions: InterviewQuestion[];
    transitionMessage: string;
    isExpanded: boolean;
}

interface InterviewSectionsData {
    sections: InterviewSection[];
    minTotalDuration?: number; // Minimum total interview duration in minutes
    maxTotalDuration?: number; // Maximum total interview duration in minutes
}

interface Props {
    mode: "view" | "edit";
    data: InterviewAssessment;
    onDataChange: (data: InterviewAssessment) => void;
    onValidationChange?: (isValid: boolean, errors: string[]) => void;
}

export default function InterviewSectionsComponent({
    mode,
    data,
    onDataChange,
    onValidationChange,
}: Props) {
    const snackbar = useSnackbar();
    const [aiCheckState, setAiCheckState] = useState<string>("idle");
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [draggedQuestionId, setDraggedQuestionId] = useState<number | null>(
        null
    );
    const [hoveredSectionIndex, setHoveredSectionIndex] = useState<number | null>(
        null
    );
    const [hoveredQuestionId, setHoveredQuestionId] = useState<number | null>(
        null
    );
    const [dragOverQuestionId, setDragOverQuestionId] = useState<number | null>(
        null
    );
    const [isDraggingSection, setIsDraggingSection] = useState(false);
    const [isDraggingQuestion, setIsDraggingQuestion] = useState(false);
    const [aiSuggestions, setAiSuggestions] = useState<Record<string, any>>({});
    const [highlightMaxDuration, setHighlightMaxDuration] = useState(false);
    const [showGenerateDialog, setShowGenerateDialog] = useState(false);
    const [showMaxDurationError, setShowMaxDurationError] = useState(false);
    const [showMinDurationError, setShowMinDurationError] = useState(false);
    const maxDurationInputRef = useRef<HTMLInputElement>(null);

    const handleGenerateClick = () => {
        // Check if max duration is set
        if (!data.content.maxDurationInMinutes) {
            setShowMaxDurationError(true);
            return;
        }

        // Clear any previous error and show the dialog to get user's prompt
        setShowMaxDurationError(false);
        setShowGenerateDialog(true);
    };

    const handleGenerateInterview = (prompt: string) => {
        // Start loading state
        setAiCheckState("loading");

        generateInterviewSections({
            requirements: prompt,
            max_duration: data.content.maxDurationInMinutes || 15,
            interview_context: {
                toneAndCommunication: {
                    enabled: data.content.toneAndCommunication?.enabled || false,
                    selectedTones: data.content.toneAndCommunication?.selectedTones || []
                }
            }
        }).then((resp) => {
            if (resp.status === 200) {
                const sections: Section[] = resp.data.data.sections.map((section: GenerateInterviewSectionResponse) => {
                    const startTime = data.content.sections.length > 0 ?
                        data.content.sections[data.content.sections.length - 1].endTime : 0;
                    const endTime = startTime + section.duration;

                    return {
                        ...section,
                        instruction: section.objective,
                        transition: section.transition_message,
                        duration_in_minutes: section.duration,
                        duration: section.duration * 60,
                        startTime: startTime,
                        endTime: endTime,
                        is_expanded: section.number === 1 ? true : false,
                        questions: (section.questions || [] as string[]).map((q) => ({ text: q, is_expanded: false, followUpInstructions: [] }))
                    }
                });

                onDataChange({
                    ...data,
                    content: {
                        ...data.content,
                        sections: sections,
                    }
                });
                setAiCheckState("completed");
            }
        }).catch((error) => {
            catchErrorLog(`Error generating interview sections: ${error}`);
            snackbar.showError("Failed to generate interview sections. Please try again.");
        }).finally(() => {
            setAiCheckState("idle");
        });
    };

    // Calculate total duration from all sections
    const totalSectionsDuration = data.content.sections.reduce(
        (total, section) => total + (section.duration_in_minutes ? section.duration_in_minutes : Math.ceil(section.duration / 60)),
        0
    );

    // Check if total duration is within the specified range
    const isDurationValid = () => {
        if (!data.content.maxDurationInMinutes) return false;
        if ((data.content.sections || []).length === 0) return false;

        if (data.content.maxDurationInMinutes < 5) {
            return false;
        }

        if (
            data.content.maxDurationInMinutes &&
            totalSectionsDuration > data.content.maxDurationInMinutes
        ) {
            return false;
        }

        return true;
    };

    const getDurationValidationMessage = () => {
        if (!data.content.maxDurationInMinutes) return null;
        if ((data.content.sections || []).length === 0) return null;

        if (
            data.content.maxDurationInMinutes &&
            totalSectionsDuration > data.content.maxDurationInMinutes
        ) {
            const difference = totalSectionsDuration - data.content.maxDurationInMinutes;
            const maxDurationText = `${data.content.maxDurationInMinutes} ${data.content.maxDurationInMinutes === 1 ? "minute" : "minutes"}`;

            return {
                type: "over" as const,
                message: `Your sections total ${totalSectionsDuration} ${totalSectionsDuration === 1 ? "minute" : "minutes"}, which is ${difference} ${difference === 1 ? "minute" : "minutes"} over the maximum of ${maxDurationText}.`,
                messageWithLink: (
                    <>
                        Your sections total {totalSectionsDuration}{" "}
                        {totalSectionsDuration === 1 ? "minute" : "minutes"}, which is{" "}
                        {difference} {difference === 1 ? "minute" : "minutes"} over the
                        maximum of{" "}
                        <button
                            onClick={() => {
                                setHighlightMaxDuration(true);
                                maxDurationInputRef.current?.focus();
                                maxDurationInputRef.current?.scrollIntoView({
                                    behavior: "smooth",
                                    block: "center",
                                });
                                setTimeout(() => setHighlightMaxDuration(false), 2000);
                            }}
                            className="underline hover:text-red-800 cursor-pointer font-semibold"
                        >
                            {maxDurationText}
                        </button>
                        .
                    </>
                ),
            };
        }

        return {
            type: "valid" as const,
            message: `Your sections total ${totalSectionsDuration} ${totalSectionsDuration === 1 ? "minute" : "minutes"}, which is within your targeted max duration.`,
            messageWithLink: null,
        };
    };

    // Calculate progress checklist
    const hasAtLeastTwoSections = data.content.sections.length >= 2;
    const allSectionsHaveInstructions = data.content.sections.length > 0 ? data.content.sections.every(
        (s) => s.instruction && s.instruction.trim()
    ) : false;

    const allSectionsHaveAtLeastOneQuestion = data.content.sections.length > 0 ? data.content.sections.every(
        (s) => (s.questions || []).length >= 1
    ) : false;

    const isMaxDurationSet = data.content.maxDurationInMinutes !== undefined && data.content.maxDurationInMinutes > 0 && data.content.maxDurationInMinutes >= 5;

    const checklistItems = [
        {
            label: "Set your Maximum Interview Duration",
            completed: isMaxDurationSet,
        },
        {
            label: "Create at least 2 interview sections",
            completed: hasAtLeastTwoSections,
        },
        {
            label: "Set your Section Objective",
            completed: allSectionsHaveInstructions,
        },

        {
            label: "Add at least 1 question to your interview section",
            completed: allSectionsHaveAtLeastOneQuestion,
        },
    ];

    const completedCount = checklistItems.filter((item) => item.completed).length;
    const totalCount = checklistItems.length;
    const progressPercentage = Math.round((completedCount / totalCount) * 100);

    // Validation logic
    const validateSections = () => {
        const errors: string[] = [];

        // Check duration validation
        if (!isDurationValid()) {
            const validationMsg = getDurationValidationMessage();
            if (validationMsg) {
                errors.push(validationMsg.message);
            }
        }

        if (data.content.interviewName.trim().length === 0) {
            errors.push("Interview name is required.");
        }

        if (data.content.description.trim().length === 0) {
            errors.push("Interview description is required.");
        }

        if (!data.content.interviewLanguage) {
            errors.push("Interview language is required.");
        }

        if (data.content.interviewerPersona && data.content.interviewerPersona.trim().length === 0) {
            errors.push("Interviewer persona is required.");
        }

        if (data.content.toneAndCommunication && data.content.toneAndCommunication.selectedTones?.length === 0) {
            errors.push("At least one Tone & Communication style must be selected.");
        }

        if (data.content.interviewFlowPrinciples && data.content.interviewFlowPrinciples.selectedPrinciples?.length === 0) {
            errors.push("At least one Interview Flow Principle must be selected.");
        }

        if (data.content.maxDurationInMinutes === undefined || data.content.maxDurationInMinutes === 0) {
            errors.push("Maximum interview duration must be set.");
        }

        if (data.content.sections.length === 0) {
            errors.push("At least one section is required.");
        }

        data.content.sections.forEach((section, index) => {
            // Check if section has objective/instructions
            if (!section.instruction || !section.instruction.trim()) {
                errors.push(
                    `Section ${index + 1} (${section.title || "Untitled"}): Missing Section Objective`
                );
            }

            // Check if section has at least one question
            if ((section.questions || []).length === 0) {
                errors.push(
                    `Section ${index + 1} (${section.title || "Untitled"}): Must have at least 1 question`
                );
            }

            // Check if all questions have content
            (section.questions || []).forEach((question, qIndex) => {
                if (!question.text || !question.text.trim()) {
                    errors.push(
                        `Section ${index + 1} (${section.title || "Untitled"}), Question ${qIndex + 1}: Question text is required`
                    );
                }
            });
        });

        const isValid = errors.length === 0;

        // Notify parent component about validation state
        if (onValidationChange) {
            onValidationChange(isValid, errors);
        }

        return { isValid, errors };
    };

    // Run validation whenever data changes
    useEffect(() => {
        validateSections();
    }, [data, mode]);

    const updateSection = (sectionIndex: number, field: string, value: any) => {
        const updatedSections: Section[] = data.content.sections.map((section, index) =>
            index === sectionIndex ? { ...section, [field]: value } : section
        );
        onDataChange({ ...data, content: { ...data.content, sections: updatedSections } });
    };

    const addSection = () => {
        const newSection: Section = {
            id: "",
            title: "",
            type: "speak",
            duration: 1, // Default 1 minute duration
            instruction: "",
            questions: [],
            transition: "",
            startTime: 0,
            endTime: 0,
            is_expanded: true,
        };
        onDataChange({ ...data, content: { ...data.content, sections: [...data.content.sections, newSection] } });
    };

    const removeSection = (sectionIndex: number) => {
        const updatedSections = data.content.sections.filter((s, index) => index !== sectionIndex);
        onDataChange({ ...data, content: { ...data.content, sections: updatedSections } });
    };

    const addQuestion = (sectionIndex: number) => {
        const updatedSections = data.content.sections.map((section, index) =>
            index === sectionIndex
                ? {
                    ...section,
                    questions: [
                        ...(section.questions || []),
                        {
                            id: "",
                            text: ""
                        },
                    ],
                }
                : section
        );
        onDataChange({ ...data, content: { ...data.content, sections: updatedSections } });
    };

    const removeQuestion = (sectionIndex: number, questionIndex: number) => {
        const updatedSections = data.content.sections.map((section, index) =>
            index === sectionIndex
                ? {
                    ...section,
                    questions: (section.questions || []).filter((q, qIndex) => qIndex !== questionIndex),
                }
                : section
        );
        onDataChange({ ...data, content: { ...data.content, sections: updatedSections } });
    };

    const updateQuestion = (
        sectionIndex: number,
        questionIndex: number,
        value: string
    ) => {
        const updatedSections = data.content.sections.map((section, index) =>
            index === sectionIndex
                ? {
                    ...section,
                    questions: (section.questions || []).map((q, qIndex) =>
                        qIndex === questionIndex ? { ...q, text: value } : q
                    ),
                }
                : section
        );
        onDataChange({ ...data, content: { ...data.content, sections: updatedSections } });
    };

    const toggleQuestion = (sectionIndex: number, questionIndex: number) => {
        const updatedSections = data.content.sections.map((section, index) =>
            index === sectionIndex
                ? {
                    ...section,
                    questions: (section.questions || []).map((q, qIndex) =>
                        qIndex === questionIndex
                            ? { ...q, is_expanded: !q.is_expanded }
                            : q
                    ),
                }
                : section
        );
        updateSection(sectionIndex, "questions", updatedSections[sectionIndex].questions);
    };

    const addFollowUpInstruction = (sectionIndex: number, questionIndex: number) => {
        const updatedSections = data.content.sections.map((section, index) =>
            index === sectionIndex
                ? {
                    ...section,
                    questions: (section.questions || []).map((q, qIndex) =>
                        qIndex === questionIndex
                            ? {
                                ...q,
                                followUpInstructions: [
                                    ...(q.followUpInstructions || []),
                                    "",
                                ],
                            }
                            : q
                    ),
                }
                : section

        );
        onDataChange({ ...data, content: { ...data.content, sections: updatedSections } });
    };

    const removeFollowUpInstruction = (
        sectionIndex: number,
        questionIndex: number,
        followUpIndex: number
    ) => {
        const updatedSections = data.content.sections.map((section, index) =>
            index === sectionIndex
                ? {
                    ...section,
                    questions: (section.questions || []).map((q, qIndex) =>
                        qIndex === questionIndex
                            ? {
                                ...q,
                                followUpInstructions: (q.followUpInstructions || []).filter(
                                    (_, index) => index !== followUpIndex
                                ),
                            }
                            : q
                    ),
                }
                : section
        );
        onDataChange({ ...data, content: { ...data.content, sections: updatedSections } });
    };

    const updateFollowUpInstruction = (
        sectionIndex: number,
        questionIndex: number,
        followUpIndex: number,
        value: string
    ) => {
        const updatedSections = data.content.sections.map((section, index) =>
            index === sectionIndex
                ? {
                    ...section,
                    questions: (section.questions || []).map((q, qIndex) =>
                        qIndex === questionIndex
                            ? {
                                ...q,
                                followUpInstructions: (q.followUpInstructions || []).map(
                                    (instruction, index) =>
                                        index === followUpIndex ? value : instruction
                                ),
                            }
                            : q
                    ),
                }
                : section
        );
        onDataChange({ ...data, content: { ...data.content, sections: updatedSections } });
    };

    const toggleSection = (sectionIndex: number) => {
        updateSection(
            sectionIndex,
            "is_expanded",
            !data.content.sections.find((s, index) => index === sectionIndex)?.is_expanded
        );
    };

    const handleDragStart = (e: React.DragEvent, index: number) => {
        if (!isDraggingSection) {
            e.preventDefault();
            return;
        }
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
        // Add a slight opacity to the dragged element
        if (e.currentTarget instanceof HTMLElement) {
            e.currentTarget.style.opacity = "0.5";
        }
    };

    const handleDragEnd = (e: React.DragEvent) => {
        if (e.currentTarget instanceof HTMLElement) {
            e.currentTarget.style.opacity = "1";
        }
        setDraggedIndex(null);
        setDragOverIndex(null);
        setIsDraggingSection(false);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";

        if (draggedIndex !== null && draggedIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();

        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDragOverIndex(null);
            return;
        }

        const newSections = [...data.content.sections];
        const [draggedSection] = newSections.splice(draggedIndex, 1);
        newSections.splice(dropIndex, 0, draggedSection);

        onDataChange({ ...data, content: { ...data.content, sections: newSections } });
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    return (
        <div style={{ paddingTop: "24px", paddingBottom: "40px" }}>
            {/* Two-Panel Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left Panel: Header and Description */}
                <div className="lg:col-span-3 flex flex-col space-y-4">
                    {/* Title with Step Badge */}
                    <div className="space-y-3">
                        <div className="flex items-start justify-between gap-3 pr-[5px]">
                            <div
                                className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30"
                                style={{ backgroundColor: "#dbeafe", color: "#2563eb" }}
                            >
                                <ListOrdered
                                    className="w-5 h-5 text-blue-600 dark:text-blue-400"
                                    style={{ color: "#2563eb" }}
                                />
                            </div>
                            <div className="flex-1">
                                <div className="space-y-1">
                                    <div className="flex items-start justify-between gap-2">
                                        <h2 className="text-lg font-semibold">
                                            Interview Sections
                                        </h2>
                                        <span
                                            className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded"
                                            style={{
                                                paddingTop: "4px",
                                                paddingBottom: "4px",
                                                borderRadius: "1rem",
                                            }}
                                        >
                                            Step 4
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                        <span>
                                            {data.content.sections.length}{" "}
                                            {data.content.sections.length === 1 ? "section" : "sections"}
                                        </span>
                                        <span>•</span>
                                        <span>
                                            {data.content.sections.reduce(
                                                (total, section) => total + (section.questions || []).length,
                                                0
                                            )}{" "}
                                            {data.content.sections.reduce(
                                                (total, section) => total + (section.questions || []).length,
                                                0
                                            ) === 1
                                                ? "question"
                                                : "questions"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-muted-foreground">
                        Organize your interview into focused sections that reflect your
                        screening goals.
                    </p>

                    {/* Compact Tip */}
                    <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                        <div className="flex gap-2">
                            <Lightbulb className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />

                            <p className="text-xs text-blue-900">
                                <span className="font-semibold">Tip:</span> A great AI interview
                                usually has 4-5 sections with clear objectives. You can edit or
                                reorder them anytime.
                            </p>
                        </div>
                    </div>

                    {/* Duration Range Inputs - Moved to left panel - Only in Edit Mode */}
                    {mode === "edit" ? (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <div
                                    className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100"
                                    style={{ backgroundColor: "#dbeafe", color: "#2563eb" }}
                                >
                                    <Clock
                                        className="w-4 h-4 text-blue-600"
                                        style={{ color: "#2563eb" }}
                                    />
                                </div>
                                <h3 className="text-base font-medium text-gray-900">
                                    Maximum Interview Duration Target
                                </h3>
                            </div>
                            <p className="text-xs text-gray-500">
                                Set the maximum total duration for your interview. As you add sections, we'll make sure their combined time stays within this limit. <br /><br />
                                This limit covers only your structured sections. If candidates ask questions at any point, the interview may extend slightly longer.
                            </p>
                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-gray-700">
                                    Max Duration (minutes)
                                </Label>
                                <Input
                                    type="number"
                                    ref={maxDurationInputRef}
                                    value={data.content.maxDurationInMinutes}
                                    onChange={(e) => {
                                        const inputValue = parseInt(e.target.value);
                                        if (inputValue > 30) return; // Enforce max limit of 30
                                        onDataChange({ ...data, content: { ...data.content, maxDurationInMinutes: inputValue } });
                                        // Clear error when user starts typing
                                        if (showMaxDurationError) {
                                            setShowMaxDurationError(false);
                                        }
                                    }}
                                    onBlur={(e) => {
                                        const inputValue = parseInt(e.target.value);
                                        if (inputValue < 10) {
                                            setShowMinDurationError(true);
                                        } else {
                                            setShowMinDurationError(false);
                                        }
                                    }}
                                    placeholder="e.g., 15"
                                    min="5"
                                    max="30"
                                    className={
                                        showMaxDurationError || highlightMaxDuration
                                            ? "border-red-500 focus-visible:ring-red-500"
                                            : ""
                                    }
                                />

                                {showMaxDurationError && (
                                    <p className="text-sm text-red-600 font-medium">
                                        Set a max duration time.
                                    </p>
                                )}
                                {showMinDurationError && (
                                    <p className="text-sm text-red-600 font-medium">
                                        Min duration is 5 minutes.
                                    </p>
                                )}
                            </div>
                        </div>
                    ) : (
                        // View mode - Show max duration with same style as section objective
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-gray-700">
                                Max Duration (minutes)
                            </Label>
                            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <p
                                    className="text-sm text-gray-500"
                                    style={{ color: "#9ca3af" }}
                                >
                                    {data.content.maxDurationInMinutes
                                        ? `${data.content.maxDurationInMinutes} ${data.content.maxDurationInMinutes === 1 ? "minute" : "minutes"}`
                                        : "No duration set"}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Spacer to push progress checklist to bottom */}
                    <div className="flex-1" />

                    {/* Progress Checklist - Moved to bottom */}
                    <div className="mt-auto space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                Complete your Interview Sections
                            </h3>
                            <span className="text-xs text-blue-600 dark:text-blue-400">
                                {completedCount}/{totalCount} completed
                            </span>
                        </div>

                        {/* Progress Bar */}
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                            <div
                                className="bg-blue-600 dark:bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${progressPercentage}%` }}
                            />
                        </div>

                        {/* Checklist Items */}
                        <div className="space-y-2.5 pt-1">
                            {checklistItems.map((item, index) => (
                                <div key={index} className="flex items-start gap-2.5">
                                    <div className="mt-0.5">
                                        {item.completed ? (
                                            <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                                        ) : (
                                            <Circle className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                                        )}
                                    </div>
                                    <span
                                        className={`text-sm leading-tight ${item.completed ? "text-gray-700 dark:text-gray-300" : "text-gray-500 dark:text-gray-400"}`}
                                    >
                                        {item.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Panel: Sections List */}
                <div className="lg:col-span-9 space-y-4">
                    {/* Loading state */}
                    {aiCheckState === "loading" ? (
                        <div className="w-full h-full min-h-[400px] flex flex-col items-center justify-center">
                            <div className="flex space-x-2 mb-4">
                                {/* Three bouncing blue dots */}
                                <div
                                    className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                    style={{ animationDelay: "0ms" }}
                                ></div>
                                <div
                                    className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                    style={{ animationDelay: "150ms" }}
                                ></div>
                                <div
                                    className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                    style={{ animationDelay: "300ms" }}
                                ></div>
                            </div>
                            <p className="text-gray-600 text-sm">
                                Generating your interview sections...
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Global AI Suggestions */}
                            {(aiCheckState === "completed" && (aiSuggestions.totalDuration || aiSuggestions.sectionOrder || aiSuggestions.unevenPacing)) && (
                                <div className="space-y-3 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Sparkles className="w-4 h-4 text-purple-600" />

                                        <h4 className="text-sm font-semibold text-purple-900">
                                            Interview-Level AI Suggestions
                                        </h4>
                                    </div>
                                    {aiSuggestions.totalDuration && (
                                        <div className="bg-white p-3 rounded-lg border border-purple-200 space-y-2">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1">
                                                    <p
                                                        className={`text-sm font-medium ${aiSuggestions.totalDuration.type === "error"
                                                            ? "text-red-700"
                                                            : aiSuggestions.totalDuration.type ===
                                                                "warning"
                                                                ? "text-yellow-700"
                                                                : "text-blue-700"
                                                            }`}
                                                    >
                                                        {aiSuggestions.totalDuration.message}
                                                    </p>
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        {aiSuggestions.totalDuration.fix}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => { }}
                                                className="h-7 px-3 text-xs"
                                            >
                                                Ignore
                                            </Button>
                                        </div>
                                    )}
                                    {aiSuggestions.sectionOrder && (
                                        <div className="bg-white p-3 rounded-lg border border-purple-200 space-y-2">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-yellow-700">
                                                        {aiSuggestions.sectionOrder.message}
                                                    </p>
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        {aiSuggestions.sectionOrder.fix}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => { }}
                                                className="h-7 px-3 text-xs"
                                            >
                                                Ignore
                                            </Button>
                                        </div>
                                    )}
                                    {aiSuggestions.unevenPacing && (
                                        <div className="bg-white p-3 rounded-lg border border-purple-200 space-y-2">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-blue-700">
                                                        {aiSuggestions.unevenPacing.message}
                                                    </p>
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        {aiSuggestions.unevenPacing.fix}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => { }}
                                                className="h-7 px-3 text-xs"
                                            >
                                                Ignore
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Total Duration Component with Validation */}
                            <div>
                                <div className="flex items-center justify-between">
                                    {/* Duration Summary - Status Chip Design */}
                                    <div className="flex items-center gap-2">
                                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border bg-blue-50 border-blue-200 text-blue-700">
                                            <Clock className="w-4 h-4 flex-shrink-0 text-blue-600" />

                                            <span className="text-sm font-semibold leading-none">
                                                Total Interview Duration:
                                            </span>
                                            <span className="text-sm font-semibold leading-none">
                                                {totalSectionsDuration}{" "}
                                                {totalSectionsDuration === 1 ? "minute" : "minutes"}
                                            </span>
                                        </div>
                                    </div>
                                    {mode === "edit" && (
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                type="button"
                                                onClick={handleGenerateClick}
                                                disabled={aiCheckState === "loading"}
                                                className="h-9 px-4 text-sm font-medium transition-colors border border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                                                style={{
                                                    border: "1px solid #2563eb",
                                                }}
                                            >
                                                <Sparkles className="w-4 h-4" />
                                                {aiCheckState === "loading"
                                                    ? "Generating..."
                                                    : "Generate Interview Sections"}
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {/* Duration Validation Message */}
                                {!showMinDurationError && (
                                    <>
                                        {!isDurationValid() && getDurationValidationMessage() && (
                                            <div
                                                className={`mt-2 px-4 py-3 ${getDurationValidationMessage()?.type === "over" ? "" : ""
                                                    }`}
                                            >
                                                <p
                                                    className={`text-sm font-medium ${getDurationValidationMessage()?.type === "over"
                                                        ? "text-red-700"
                                                        : "text-yellow-800"
                                                        }`}
                                                >
                                                    {getDurationValidationMessage()?.messageWithLink ||
                                                        getDurationValidationMessage()?.message}
                                                </p>
                                            </div>
                                        )}

                                        {isDurationValid() && data.content.maxDurationInMinutes && (
                                            <div className="mt-2 px-4 py-3">
                                                <p className="text-sm font-medium text-green-700">
                                                    ✓ {getDurationValidationMessage()?.message}
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            {(data.content.sections || []).map((section, index) => (
                                <div key={index} className="relative">
                                    {/* Drop indicator line - shows above the section when dragging over */}
                                    {dragOverIndex === index &&
                                        draggedIndex !== null &&
                                        draggedIndex !== index && (
                                            <div
                                                className="absolute -top-2 left-0 right-0 h-1 bg-blue-500 rounded-full z-10"
                                                style={{ boxShadow: "0 0 8px rgba(59, 130, 246, 0.5)" }}
                                            />
                                        )}
                                    <div
                                        draggable={mode === "edit"}
                                        onDragStart={(e) => handleDragStart(e, index)}
                                        onDragEnd={handleDragEnd}
                                        onDragOver={(e) => handleDragOver(e, index)}
                                        onDragLeave={handleDragLeave}
                                        onDrop={(e) => handleDrop(e, index)}
                                        className={`border rounded-lg overflow-hidden transition-all ${dragOverIndex === index && draggedIndex !== index
                                            ? "bg-blue-50/50"
                                            : ""
                                            } ${draggedIndex === index
                                                ? "border-blue-500 shadow-lg"
                                                : hoveredSectionIndex === index && mode === "edit"
                                                    ? "border-gray-400 shadow-sm"
                                                    : "border-gray-300"
                                            }`}
                                        style={{
                                            border:
                                                draggedIndex === index
                                                    ? "1px solid #3b82f6"
                                                    : hoveredSectionIndex === index && mode === "edit"
                                                        ? "1px solid #9ca3af"
                                                        : "1px solid #d1d5db",
                                        }}
                                        onMouseEnter={() =>
                                            mode === "edit" && setHoveredSectionIndex(index)
                                        }
                                        onMouseLeave={() => setHoveredSectionIndex(null)}
                                    >
                                        <Collapsible
                                            open={section.is_expanded}
                                            onOpenChange={() => toggleSection(index)}
                                        >
                                            {/* Section Header */}
                                            <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
                                                <div className="flex items-center gap-3">
                                                    {/* Drag Handle */}
                                                    {mode === "edit" && (
                                                        <div
                                                            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation();
                                                                setIsDraggingSection(true);
                                                            }}
                                                            onMouseUp={() => setIsDraggingSection(false)}
                                                        >
                                                            <GripVertical className="h-5 w-5" />
                                                        </div>
                                                    )}
                                                    <div
                                                        className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full text-sm font-medium border border-blue-200 flex-shrink-0"
                                                        style={{
                                                            border: "1px solid #bfdbfe",
                                                            minWidth: "2rem",
                                                            minHeight: "2rem",
                                                        }}
                                                    >
                                                        {index + 1}
                                                    </div>

                                                    {mode === "edit" ? (
                                                        <Input
                                                            value={section.title}
                                                            onChange={(e) =>
                                                                updateSection(
                                                                    index,
                                                                    "title",
                                                                    e.target.value
                                                                )
                                                            }
                                                            placeholder="Section title"
                                                            className="font-medium bg-white w-full min-w-[300px] flex-1"
                                                        />
                                                    ) : (
                                                        <h3 className="font-semibold text-gray-900">
                                                            {section.title || `Section ${index + 1}`}
                                                        </h3>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-4">
                                                    {/* Section Duration Display */}
                                                    <div className="flex items-center gap-2">
                                                        {mode === "edit" ? (
                                                            <div
                                                                className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-md"
                                                                style={{ borderRadius: "1.5rem" }}
                                                            >
                                                                <span className="text-sm text-blue-900 font-medium">
                                                                    Duration:
                                                                </span>
                                                                <Input
                                                                    type="number"
                                                                    value={(section.duration_in_minutes ? section.duration_in_minutes : Math.ceil(section.duration / 60)) || ""}
                                                                    onChange={(e) => {
                                                                        const duration = parseInt(e.target.value) || 1;
                                                                        if (duration > 10) return;
                                                                        updateSection(
                                                                            index,
                                                                            "duration_in_minutes",
                                                                            duration
                                                                        )
                                                                    }}
                                                                    className="w-12 text-center border-none bg-transparent focus-visible:ring-0 p-0 h-6 font-semibold text-blue-700"
                                                                    min="1"
                                                                    max="10"
                                                                />

                                                                <span className="text-sm text-blue-900 font-medium">
                                                                    {(section.duration_in_minutes ? section.duration_in_minutes : Math.ceil(section.duration / 60)) === 1
                                                                        ? "minute"
                                                                        : "minutes"}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-md"
                                                                style={{ borderRadius: "1rem" }}
                                                            >
                                                                <span className="text-sm text-blue-900 font-medium">
                                                                    Section Duration:
                                                                </span>
                                                                <span className="text-sm text-blue-700 font-semibold">
                                                                    {(section.duration_in_minutes ? section.duration_in_minutes : Math.ceil(section.duration / 60)) || ""}{" "}
                                                                    {(section.duration_in_minutes ? section.duration_in_minutes : Math.ceil(section.duration / 60)) === 1
                                                                        ? "minute"
                                                                        : "minutes"}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="flex items-center gap-1">
                                                        {mode === "edit" && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => removeSection(index)}
                                                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        )}

                                                        <CollapsibleTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-8 w-8 p-0"
                                                            >
                                                                {section.is_expanded ? (
                                                                    <ChevronUp className="h-4 w-4" />
                                                                ) : (
                                                                    <ChevronDown className="h-4 w-4" />
                                                                )}
                                                            </Button>
                                                        </CollapsibleTrigger>
                                                    </div>
                                                </div>
                                            </div>

                                            <CollapsibleContent>
                                                <div className="p-6 space-y-6">
                                                    {/* Duration AI Suggestion */}
                                                    {aiCheckState === "completed" &&
                                                        aiSuggestions[`section_${index}`]
                                                            ?.duration && (
                                                            <div className="p-3 border rounded-lg bg-blue-50 border-blue-200 flex items-center gap-2">
                                                                <Sparkles className="w-4 h-4 text-blue-700 flex-shrink-0" />
                                                                <p className="text-sm text-blue-700 flex-1">
                                                                    {aiSuggestions[`section_${index}`].duration.fix}
                                                                </p>
                                                            </div>
                                                        )}

                                                    {/* Instructions */}
                                                    <div className="space-y-2">
                                                        <Label className="text-base font-medium flex items-center gap-2">
                                                            <Target className="w-4 h-4 text-blue-600" />
                                                            <div>
                                                                Section Objective
                                                                <span className="text-red-500">*</span>
                                                            </div>
                                                        </Label>
                                                        {mode === "edit" && (
                                                            <p className="text-xs text-gray-500">
                                                                Describe what this section aims to evaluate.
                                                                Keep it detailed and specific so the AI
                                                                understands the main goal of this part of the
                                                                interview.
                                                            </p>
                                                        )}
                                                        {mode === "edit" ? (
                                                            <Textarea
                                                                value={section.instruction}
                                                                onChange={(e) =>
                                                                    updateSection(
                                                                        index,
                                                                        "instruction",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                placeholder="Define the purpose of this section, for example: 'Assess the candidate's background and communication style.'"
                                                                className={`min-h-[160px] bg-white resize-y ${!section.instruction.trim()
                                                                    ? "border-red-300 focus-visible:ring-red-500"
                                                                    : ""
                                                                    }`}
                                                                required
                                                            />
                                                        ) : (
                                                            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                                <p className="text-sm text-gray-900">
                                                                    {renderTextWithLineBreaks(section.instruction)}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Objective AI Suggestion */}
                                                    {aiCheckState === "completed" &&
                                                        aiSuggestions[`section_${index}`]
                                                            ?.objective && (
                                                            <div className="p-3 border rounded-lg bg-blue-50 border-blue-200 flex items-center gap-2">
                                                                <Sparkles className="w-4 h-4 text-blue-700 flex-shrink-0" />

                                                                <p className="text-sm text-blue-700 flex-1">{aiSuggestions[`section_${index}`]
                                                                    .objective.fix}</p>
                                                            </div>
                                                        )}

                                                    {/* Questions */}
                                                    <div className="space-y-2">
                                                        <Label className="text-base font-medium flex items-center gap-2">
                                                            <MessageSquare className="w-4 h-4 text-blue-600" />
                                                            <div>
                                                                Interview Questions
                                                                <span className="text-red-500">*</span>
                                                            </div>
                                                        </Label>
                                                        {mode === "edit" && (
                                                            <p className="text-xs text-gray-500">
                                                                Add questions you want the AI to ask during this
                                                                section. We recommend open-ended questions that
                                                                encourage candidates to share specific stories
                                                                or skills.
                                                            </p>
                                                        )}

                                                        {(section.questions || []).length === 0 ? (
                                                            <div
                                                                className={`text-sm italic ${mode === "edit"
                                                                    ? "text-red-500 font-medium"
                                                                    : "text-gray-500"
                                                                    }`}
                                                            >
                                                                {mode === "edit"
                                                                    ? "At least one question is required."
                                                                    : "No questions added"}
                                                            </div>
                                                        ) : (
                                                            <div className="space-y-3">
                                                                {(section.questions || []).map(
                                                                    (question, questionIndex) => (
                                                                        <div key={questionIndex} className="relative">
                                                                            {/* Drop indicator line for questions */}
                                                                            {dragOverQuestionId === questionIndex &&
                                                                                draggedQuestionId !== null &&
                                                                                draggedQuestionId !== questionIndex && (
                                                                                    <div
                                                                                        className="absolute -top-1.5 left-0 right-0 h-1 bg-blue-500 rounded-full z-10"
                                                                                        style={{
                                                                                            boxShadow:
                                                                                                "0 0 8px rgba(59, 130, 246, 0.5)",
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            <div
                                                                                draggable={mode === "edit"}
                                                                                onDragStart={(e) => {
                                                                                    if (!isDraggingQuestion) {
                                                                                        e.preventDefault();
                                                                                        return;
                                                                                    }
                                                                                    e.stopPropagation();
                                                                                    setDraggedQuestionId(questionIndex);
                                                                                    e.dataTransfer.setData(
                                                                                        "questionIndex",
                                                                                        questionIndex.toString()
                                                                                    );
                                                                                    e.dataTransfer.setData(
                                                                                        "sectionIndex",
                                                                                        index.toString()
                                                                                    );
                                                                                    e.dataTransfer.effectAllowed = "move";
                                                                                    if (
                                                                                        e.currentTarget instanceof
                                                                                        HTMLElement
                                                                                    ) {
                                                                                        e.currentTarget.style.opacity =
                                                                                            "0.5";
                                                                                    }
                                                                                }}
                                                                                onDragEnd={(e) => {
                                                                                    if (
                                                                                        e.currentTarget instanceof
                                                                                        HTMLElement
                                                                                    ) {
                                                                                        e.currentTarget.style.opacity = "1";
                                                                                    }
                                                                                    setDraggedQuestionId(null);
                                                                                    setDragOverQuestionId(null);
                                                                                    setIsDraggingQuestion(false);
                                                                                }}
                                                                                onDragOver={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    e.dataTransfer.dropEffect = "move";

                                                                                    const draggedQuestionIndexFromData =
                                                                                        parseInt(
                                                                                            e.dataTransfer.getData(
                                                                                                "questionIndex"
                                                                                            ) || "-1"
                                                                                        );
                                                                                    if (
                                                                                        draggedQuestionIndexFromData !== -1 &&
                                                                                        draggedQuestionIndexFromData !==
                                                                                        questionIndex
                                                                                    ) {
                                                                                        setDragOverQuestionId(questionIndex);
                                                                                    }
                                                                                }}
                                                                                onDragLeave={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setDragOverQuestionId(null);
                                                                                }}
                                                                                onDrop={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    const draggedQuestionIndexFromData =
                                                                                        parseInt(
                                                                                            e.dataTransfer.getData(
                                                                                                "questionIndex"
                                                                                            )
                                                                                        );
                                                                                    const draggedSectionIndex = parseInt(
                                                                                        e.dataTransfer.getData("sectionIndex")
                                                                                    );

                                                                                    if (
                                                                                        draggedSectionIndex === index &&
                                                                                        draggedQuestionIndexFromData !==
                                                                                        questionIndex &&
                                                                                        !isNaN(draggedQuestionIndexFromData)
                                                                                    ) {
                                                                                        const updatedSections =
                                                                                            data.content.sections.map((s, sIndex) => {
                                                                                                if (sIndex === index) {
                                                                                                    const questions = [
                                                                                                        ...(s.questions || []),
                                                                                                    ];

                                                                                                    const [draggedQuestion] =
                                                                                                        questions.splice(
                                                                                                            draggedQuestionIndexFromData,
                                                                                                            1
                                                                                                        );
                                                                                                    questions.splice(
                                                                                                        questionIndex,
                                                                                                        0,
                                                                                                        draggedQuestion
                                                                                                    );
                                                                                                    return { ...s, questions };
                                                                                                }
                                                                                                return s;
                                                                                            });

                                                                                        onDataChange({ ...data, content: { ...data.content, sections: updatedSections } });
                                                                                    }
                                                                                    setDraggedQuestionId(null);
                                                                                    setDragOverQuestionId(null);
                                                                                }}
                                                                                className={`border rounded-lg bg-gray-50 transition-all ${draggedQuestionId === questionIndex
                                                                                    ? "border-blue-500 shadow-lg"
                                                                                    : hoveredQuestionId ===
                                                                                        questionIndex && mode === "edit"
                                                                                        ? "border-gray-400 shadow-sm"
                                                                                        : "border-gray-200"
                                                                                    }`}
                                                                                style={{
                                                                                    border:
                                                                                        draggedQuestionId === questionIndex
                                                                                            ? "1px solid #3b82f6"
                                                                                            : hoveredQuestionId ===
                                                                                                questionIndex &&
                                                                                                mode === "edit"
                                                                                                ? "1px solid #9ca3af"
                                                                                                : "1px solid #e5e7eb",
                                                                                }}
                                                                                onMouseEnter={() =>
                                                                                    mode === "edit" &&
                                                                                    setHoveredQuestionId(questionIndex)
                                                                                }
                                                                                onMouseLeave={() =>
                                                                                    setHoveredQuestionId(null)
                                                                                }
                                                                            >
                                                                                <div className="flex items-start gap-3 p-3">
                                                                                    {mode === "edit" && (
                                                                                        <div
                                                                                            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 flex-shrink-0"
                                                                                            onMouseDown={(e) => {
                                                                                                e.stopPropagation();
                                                                                                setIsDraggingQuestion(true);
                                                                                            }}
                                                                                            onMouseUp={() =>
                                                                                                setIsDraggingQuestion(false)
                                                                                            }
                                                                                        >
                                                                                            <GripVertical className="h-4 w-4" />
                                                                                        </div>
                                                                                    )}

                                                                                    <div
                                                                                        className="flex items-center justify-center w-6 h-6 bg-gray-100 text-gray-600 rounded-full text-xs font-medium border border-gray-200 flex-shrink-0"
                                                                                        style={{
                                                                                            border: "1px solid #e5e7eb",
                                                                                            minWidth: "1.5rem",
                                                                                            minHeight: "1.5rem",
                                                                                        }}
                                                                                    >
                                                                                        {questionIndex + 1}
                                                                                    </div>

                                                                                    <div className="flex-1 space-y-3">
                                                                                        {/* AI Suggestion for this question */}
                                                                                        {aiCheckState === "completed" &&
                                                                                            aiSuggestions[
                                                                                                `section_${index}`
                                                                                            ]?.questions?.[
                                                                                            `question_${question.id}`
                                                                                            ] && (
                                                                                                <div className="p-3 border rounded-lg bg-blue-50 border-blue-200 flex items-center gap-2">
                                                                                                    <Sparkles className="w-4 h-4 text-blue-700 flex-shrink-0" />
                                                                                                    <p className="text-sm text-blue-700 flex-1">{aiSuggestions[
                                                                                                        `section_${index}`
                                                                                                    ].questions[
                                                                                                        `question_${question.id}`
                                                                                                    ].fix}</p>
                                                                                                </div>
                                                                                            )}
                                                                                        {mode === "edit" ? (
                                                                                            <Textarea
                                                                                                value={question.text}
                                                                                                onChange={(e) =>
                                                                                                    updateQuestion(
                                                                                                        index,
                                                                                                        questionIndex,
                                                                                                        e.target.value as string
                                                                                                    )
                                                                                                }
                                                                                                placeholder="Enter question text"
                                                                                                className={`min-h-[60px] bg-white resize-none ${!question.text.trim()
                                                                                                    ? "border-red-300 focus-visible:ring-red-500"
                                                                                                    : ""
                                                                                                    }`}
                                                                                                required
                                                                                            />
                                                                                        ) : (
                                                                                            <p className="text-sm text-gray-900">
                                                                                                {question.text ||
                                                                                                    "Question content will appear here..."}
                                                                                            </p>
                                                                                        )}

                                                                                        {/* Follow-up Instructions Section */}
                                                                                        {mode === "edit" && (
                                                                                            <Collapsible
                                                                                                open={question.is_expanded || false}
                                                                                                onOpenChange={() =>
                                                                                                    toggleQuestion(
                                                                                                        index,
                                                                                                        questionIndex
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                <div className="space-y-2">
                                                                                                    <div className="flex items-center justify-between">
                                                                                                        <div className="flex items-center gap-2">
                                                                                                            <Label className="text-sm text-gray-600">
                                                                                                                Follow-up instructions
                                                                                                            </Label>
                                                                                                            <span className="text-xs text-gray-400">
                                                                                                                Optional (Max 2
                                                                                                                follow-ups per question)
                                                                                                            </span>
                                                                                                        </div>
                                                                                                    </div>

                                                                                                    <div className="space-y-2">
                                                                                                        {(
                                                                                                            question.followUpInstructions ||
                                                                                                            []
                                                                                                        ).map(
                                                                                                            (
                                                                                                                instruction,
                                                                                                                followUpIndex
                                                                                                            ) => (
                                                                                                                <div
                                                                                                                    key={followUpIndex}
                                                                                                                    className="flex items-start gap-2"
                                                                                                                >
                                                                                                                    <Textarea
                                                                                                                        value={
                                                                                                                            instruction
                                                                                                                        }
                                                                                                                        onChange={(e) =>
                                                                                                                            updateFollowUpInstruction(
                                                                                                                                index,
                                                                                                                                questionIndex,
                                                                                                                                followUpIndex,
                                                                                                                                e.target.value
                                                                                                                            )
                                                                                                                        }
                                                                                                                        placeholder="Add follow-up instructions for the AI interviewer (e.g., 'If the answer is unclear, ask for one specific example.')"
                                                                                                                        className="min-h-[60px] resize-none bg-white flex-1"
                                                                                                                    />

                                                                                                                    <Button
                                                                                                                        variant="ghost"
                                                                                                                        size="sm"
                                                                                                                        onClick={() =>
                                                                                                                            removeFollowUpInstruction(
                                                                                                                                index,
                                                                                                                                questionIndex,
                                                                                                                                followUpIndex
                                                                                                                            )
                                                                                                                        }
                                                                                                                        className="h-8 w-8 p-0 text-gray-400 hover:text-red-700 hover:bg-red-50 flex-shrink-0 mt-1"
                                                                                                                    >
                                                                                                                        <Trash2 className="h-4 w-4" />
                                                                                                                    </Button>
                                                                                                                </div>
                                                                                                            )
                                                                                                        )}
                                                                                                        {(!question.followUpInstructions ||
                                                                                                            question
                                                                                                                .followUpInstructions
                                                                                                                .length < 2) && (
                                                                                                                <div className="flex justify-start">
                                                                                                                    <Button
                                                                                                                        onClick={() =>
                                                                                                                            addFollowUpInstruction(
                                                                                                                                index,
                                                                                                                                questionIndex
                                                                                                                            )
                                                                                                                        }
                                                                                                                        variant="outline"
                                                                                                                        size="sm"
                                                                                                                        className="flex items-center gap-2"
                                                                                                                    >
                                                                                                                        <Plus className="h-4 w-4" />
                                                                                                                        Add Follow-up
                                                                                                                        Instruction
                                                                                                                    </Button>
                                                                                                                </div>
                                                                                                            )}
                                                                                                    </div>
                                                                                                </div>
                                                                                            </Collapsible>
                                                                                        )}

                                                                                        {/* View Mode - Show follow-up if exists */}
                                                                                        {mode === "view" &&
                                                                                            question.followUpInstructions &&
                                                                                            question.followUpInstructions
                                                                                                .length > 0 && (
                                                                                                <div className="space-y-2">
                                                                                                    <Label className="text-xs text-gray-500">
                                                                                                        Follow-up instructions
                                                                                                    </Label>
                                                                                                    <div className="space-y-2">
                                                                                                        {question.followUpInstructions.map(
                                                                                                            (instruction, index) => (
                                                                                                                <p
                                                                                                                    key={index}
                                                                                                                    className="text-sm text-gray-900 bg-gray-50 p-2 rounded border border-gray-200"
                                                                                                                >
                                                                                                                    {instruction}
                                                                                                                </p>
                                                                                                            )
                                                                                                        )}
                                                                                                    </div>
                                                                                                </div>
                                                                                            )}
                                                                                    </div>

                                                                                    {mode === "edit" && (
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            onClick={() =>
                                                                                                removeQuestion(
                                                                                                    index,
                                                                                                    questionIndex
                                                                                                )
                                                                                            }
                                                                                            className="h-8 w-8 p-0 text-gray-400 hover:text-red-700 hover:bg-red-50 flex-shrink-0 mt-1"
                                                                                        >
                                                                                            <Trash2 className="h-4 w-4" />
                                                                                        </Button>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                )}
                                                            </div>
                                                        )}

                                                        {mode === "edit" && (
                                                            <Button
                                                                onClick={() => addQuestion(index)}
                                                                variant="outline"
                                                                size="sm"
                                                                className="flex items-center gap-2"
                                                            >
                                                                <Plus className="h-4 w-4" />
                                                                Add Question
                                                            </Button>
                                                        )}
                                                    </div>

                                                    {/* Transition to next section */}
                                                    {mode === "edit" && (
                                                        <div className="space-y-2 hidden">
                                                            <Label className="text-base font-medium flex items-center gap-2">
                                                                <ArrowRight className="w-4 h-4 text-blue-600" />
                                                                Section Transition Message
                                                            </Label>
                                                            <p className="text-xs text-gray-500">
                                                                Add a short message the AI will use to close
                                                                this section and smoothly lead into the next
                                                                one. This helps maintain a natural interview
                                                                flow.
                                                            </p>
                                                            <Textarea
                                                                value={section.transition}
                                                                onChange={(e) =>
                                                                    updateSection(
                                                                        index,
                                                                        "transition",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                placeholder="Thanks for sharing. Let's move to the next part of the interview."
                                                                className="min-h-[60px] resize-none"
                                                            />

                                                            {/* Transition AI Suggestion */}
                                                            {aiCheckState === "completed" &&
                                                                aiSuggestions[`section_${index}`]
                                                                    ?.transition && (
                                                                    <div className="p-3 border rounded-lg bg-blue-50 border-blue-200 flex items-center gap-2">
                                                                        <Sparkles className="w-4 h-4 text-blue-700 flex-shrink-0" />

                                                                        <p className="text-sm text-blue-700 flex-1">{aiSuggestions[`section_${index}`]
                                                                            .transition.fix}</p>
                                                                    </div>
                                                                )}
                                                        </div>
                                                    )}
                                                </div>
                                            </CollapsibleContent>
                                        </Collapsible>
                                    </div>
                                </div>
                            ))}

                            {/* Add Section Button at the bottom */}
                            {mode === "edit" && (
                                <div className="flex justify-center pt-4">
                                    <Button onClick={addSection} variant="outline" size="sm">
                                        <Plus className="h-4 w-4" />
                                        Add Section
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Generate Interview Dialog */}
            <GenerateInterviewDialog
                isOpen={showGenerateDialog}
                onClose={() => setShowGenerateDialog(false)}
                onGenerate={handleGenerateInterview}
            />
        </div>
    );
}

export type { InterviewSectionsData, InterviewSection, InterviewQuestion };
